import React, { forwardRef, useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { Flex, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useFormContextActions } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { SMART_FIELDS_ADMIN_TOGGLE_APP_PROPERTY_KEY } from '@atlassian/jira-personal-settings-gic-smart-fields/src/common/constants.tsx';
import { useSmartFieldsUpdateUserPreference } from '@atlassian/jira-personal-settings-gic-smart-fields/src/services/index.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { gicSmartFieldsFeatureResource } from '@atlassian/jira-router-resources-gic-smart-fields/src/services/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import type { SmartFieldsDropdownItemQuery as SmartFieldsAdminToggleQuery } from './__generated__/SmartFieldsDropdownItemQuery.graphql';
import messages from './messages.tsx';

type CustomToggleButtonProps = React.PropsWithChildren<{
	isChecked: boolean;
	onChange: (event: React.ChangeEvent<HTMLInputElement>, analyticsEvent: UIAnalyticsEvent) => void;
}>;

const CustomToggleButtonComponent = forwardRef<HTMLInputElement, CustomToggleButtonProps>(
	({ isChecked, onChange, ...props }, ref) => {
		const { formatMessage } = useIntl();

		return (
			<Flex alignItems="center" gap="space.150" xcss={customButtonStyles}>
				<EyeOpenStrikethroughIcon label="" />
				<Flex gap="space.100" alignItems="center">
					<label htmlFor="toggle-smart-fields">{formatMessage(messages.smartFieldsLabel)}</label>
					<Toggle
						{...props}
						id="toggle-smart-fields"
						isChecked={isChecked}
						onChange={onChange}
						ref={ref}
					/>
				</Flex>
			</Flex>
		);
	},
);

export const useSmartFieldsPreferenceResource = () => useResource(gicSmartFieldsFeatureResource);

type Props = {
	queryReference: PreloadedQuery<SmartFieldsAdminToggleQuery>;
};

export const SmartFieldsDropdownItem = ({ queryReference }: Props) => {
	const { loading, data, update: updateResource } = useSmartFieldsPreferenceResource();
	const { updatePreferences } = useSmartFieldsUpdateUserPreference();

	const { setIsSmartFieldsToggled } = useFormContextActions();

	const adminToggleStateData = usePreloadedQuery(
		graphql`
			query SmartFieldsDropdownItemQuery($cloudId: ID!) {
				jira {
					applicationPropertiesByKey(
						cloudId: $cloudId
						keys: ["jira.create.smart.fields.is-enabled"]
					) {
						key
						value
					}
				}
			}
		`,
		queryReference,
	);

	const hasUserOptedOut = useMemo(
		() => (typeof data?.hasUserOptedOut === 'boolean' ? data.hasUserOptedOut : false),
		[data],
	);

	const hasUserDismissedReadMore = useMemo(
		() =>
			typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false,
		[data],
	);

	const hasUserDismissedGicOnboarding = useMemo(
		() =>
			typeof data?.hasUserDismissedGicOnboarding === 'boolean'
				? data.hasUserDismissedGicOnboarding
				: false,
		[data],
	);

	const handleHideUnusedFieldsChange = async (
		_: React.ChangeEvent<HTMLInputElement>,
		analyticsEventUI: UIAnalyticsEvent,
	) => {
		const value = !hasUserOptedOut;
		updateResource(() => ({
			hasUserOptedOut: value,
			hasUserDismissedReadMore: false,
			hasUserDismissedGicOnboarding: false,
		}));

		fireUIAnalytics(analyticsEventUI, 'toggleGicSmartFields', {
			isSmartFieldsOptOut: value,
		});

		try {
			await updatePreferences({
				hasUserOptedOut: value,
				hasUserDismissedReadMore: false,
				hasUserDismissedGicOnboarding: false,
			});
			setIsSmartFieldsToggled(true);
		} catch (err) {
			updateResource(() => ({
				hasUserOptedOut,
				hasUserDismissedReadMore,
				hasUserDismissedGicOnboarding,
			}));
			const error =
				err instanceof Error
					? err
					: new Error('GIC Smart Fields: Unknown error while toggling user preference');
			fireErrorAnalytics({
				error,
				meta: {
					id: 'toggleGicSmartFields',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
			});
		}
	};

	const hasAdminDisabledSmartFields = useMemo(
		() =>
			adminToggleStateData?.jira?.applicationPropertiesByKey?.filter(
				(item) => item.key === SMART_FIELDS_ADMIN_TOGGLE_APP_PROPERTY_KEY,
			)[0]?.value === 'false',
		[adminToggleStateData],
	);

	if (loading || hasAdminDisabledSmartFields) {
		return null;
	}

	return (
		<DropdownItem
			// @ts-expect-error - TS2322 - Dynamic component typing is broken in the platform package
			component={CustomToggleButtonComponent}
			isChecked={!hasUserOptedOut}
			onChange={handleHideUnusedFieldsChange}
		/>
	);
};

const customButtonStyles = xcss({
	paddingInline: 'space.200',
	paddingBlock: 'space.100',
});
